import { pick } from "lodash";
import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";

import { useChatRoomContext } from "../useChatRoomContext";
import { initialChatRoomUiState } from "./slices/chatRooms/constants";
import type {
  ChatRoomUIState,
  FetchChatMessagesParams,
  ScrollInfo,
} from "./slices/chatRooms/types";
import type { ChatStoreState } from "./types";
import { useChatStore } from "./useChatStore";

const selectThreads = (state: ChatStoreState) => state.threads;
const selectChatUI = (state: ChatStoreState) => state.ui;
const selectCommunityInbox = (state: ChatStoreState) => state.inbox;
const selectUnreadChatRooms = (state: ChatStoreState) => state.unread;
const selectChatRoomMessages = (uuid: string) => (state: ChatStoreState) =>
  state.chatRoomStateMap.get(uuid)?.chatRoomMessages;
export const selectChatRoomMessageActions = (state: ChatStoreState) =>
  state.messageActions;
export const selectActions = (state: ChatStoreState) => state.actions;
export const selectChatRoom = (uuid: string) => (state: ChatStoreState) =>
  state.chatRoomMap.get(uuid);
export const selectSendRichTextMessage = (state: ChatStoreState) =>
  state.messageActions.sendRichTextMessage;
export const selectThreadsSetChatMessages = (state: ChatStoreState) =>
  state.threads.setChatMessages;
export const selectThreadsSetScrollInfo = (state: ChatStoreState) =>
  state.threads.setScrollInfo;
export const selectThreadsClearSpotlightedMessages = (state: ChatStoreState) =>
  state.threads.clearSpotlightedMessages;

export const useThreadsStore = () => useChatStore(selectThreads);
export const useChatUIStore = () => useChatStore(selectChatUI);
export const useCommunityInboxStore = () => useChatStore(selectCommunityInbox);
export const useUnreadChatRoomsStore = () =>
  useChatStore(selectUnreadChatRooms);

// Subscribes to only the requested uiState fields, so callers re-render when
// one of those fields changes rather than on every uiState write (e.g. each
// setScrollInfo).
export const useChatRoomStoreStates = <K extends keyof ChatRoomUIState>(
  ...keys: [K, ...K[]]
): Pick<ChatRoomUIState, K> => {
  const { chatRoomUuid } = useChatRoomContext();
  return useChatStore(
    useShallow(state =>
      pick(
        state.chatRoomStateMap.get(chatRoomUuid)?.uiState ??
          initialChatRoomUiState,
        keys,
      ),
    ),
  );
};

// Reads/writes `activeMessageId` for an explicit chat room instead of the one
// from `ChatRoomContext`. Thread rows need this: the threads railbar is
// rendered outside the room's `ChatRoomContextProvider` (e.g. in chat spaces),
// so the context-based hooks would target a chat room with no state.
export const useChatRoomActiveMessageId = (chatRoomUuid: string) => {
  const activeMessageId = useChatStore(
    state =>
      state.chatRoomStateMap.get(chatRoomUuid)?.uiState.activeMessageId ?? null,
  );
  const { setActiveMessageId: setChatRoomActiveMessageId } = useChatStore(
    selectChatRoomMessageActions,
  );
  const { createNewChatRoom } = useChatStore(selectActions);
  const setActiveMessageId = useCallback(
    (id: string | null) => {
      if (id && !useChatStore.getState().chatRoomStateMap.has(chatRoomUuid)) {
        createNewChatRoom(chatRoomUuid);
      }
      setChatRoomActiveMessageId(chatRoomUuid, id);
    },
    [setChatRoomActiveMessageId, chatRoomUuid, createNewChatRoom],
  );

  return { activeMessageId, setActiveMessageId };
};

// Edit mode for one message row, kept in the room's store state rather than the
// row: the virtualized list reads it to keep that row mounted while it's
// scrolled out of view. Takes the room explicitly, like
// `useChatRoomActiveMessageId`, since some rows render outside its context.
export const useChatRoomMessageEditing = (
  chatRoomUuid: string,
  messageId: number | null | undefined,
) => {
  // A boolean, so starting or ending an edit re-renders only the rows involved.
  const isEditing = useChatStore(
    state =>
      messageId != null &&
      state.chatRoomStateMap.get(chatRoomUuid)?.uiState.editingMessageId ===
        messageId,
  );
  const { setEditingMessageId: setChatRoomEditingMessageId } = useChatStore(
    selectChatRoomMessageActions,
  );
  const { createNewChatRoom } = useChatStore(selectActions);

  const setEditingMessageId = useCallback(
    (id: number | null) => {
      if (id && !useChatStore.getState().chatRoomStateMap.has(chatRoomUuid)) {
        createNewChatRoom(chatRoomUuid);
      }
      setChatRoomEditingMessageId(chatRoomUuid, id);
    },
    [setChatRoomEditingMessageId, chatRoomUuid, createNewChatRoom],
  );

  // Ends this message's edit, unless another message has taken over since.
  const stopEditing = useCallback(() => {
    const { editingMessageId } =
      useChatStore.getState().chatRoomStateMap.get(chatRoomUuid)?.uiState ??
      initialChatRoomUiState;
    if (messageId != null && editingMessageId === messageId) {
      setChatRoomEditingMessageId(chatRoomUuid, null);
    }
  }, [setChatRoomEditingMessageId, chatRoomUuid, messageId]);

  return { isEditing, setEditingMessageId, stopEditing };
};

export const useChatRoomStoreMessages = () => {
  const { chatRoomUuid } = useChatRoomContext();
  const chatRoomMessages = useChatStore(selectChatRoomMessages(chatRoomUuid));
  return {
    chatRoomMessages,
  };
};

export const useChatRoomStoreChatRoom = () => {
  const { chatRoomUuid } = useChatRoomContext();
  return useChatStore(selectChatRoom(chatRoomUuid));
};

export const useChatRoomStoreActions = () => {
  const messageActions = useChatStore(selectChatRoomMessageActions);
  const { chatRoomUuid, isMessaging } = useChatRoomContext();

  return useMemo(
    () => ({
      setChatRoomMessages: (messages: ChatRoomMessage[]) =>
        messageActions.setChatRoomMessages(chatRoomUuid, messages),
      fetchChatMessages: (params: FetchChatMessagesParams) =>
        messageActions.fetchChatMessages(chatRoomUuid, isMessaging, params),
      setIsLoading: (isLoading: boolean) =>
        messageActions.setIsLoading(chatRoomUuid, isLoading),
      setIsInitialPageLoaded: (isLoaded: boolean) =>
        messageActions.setIsInitialPageLoaded(chatRoomUuid, isLoaded),
      setHasNextPage: (hasNextPage: boolean) =>
        messageActions.setHasNextPage(chatRoomUuid, hasNextPage),
      setHasPrevPage: (hasPrevPage: boolean) =>
        messageActions.setHasPrevPage(chatRoomUuid, hasPrevPage),
      setScrollInfo: (scrollInfo: ScrollInfo) =>
        messageActions.setScrollInfo(chatRoomUuid, scrollInfo),
      setFirstMessageId: (id: number | null) =>
        messageActions.setFirstMessageId(chatRoomUuid, id),
      setLastMessageId: (id: number | null) =>
        messageActions.setLastMessageId(chatRoomUuid, id),
      setTopMessageId: (id: number | null) =>
        messageActions.setTopMessageId(chatRoomUuid, id),
      setActiveMessageId: (id: string | null) =>
        messageActions.setActiveMessageId(chatRoomUuid, id),
      appendLastMessage: (message: ChatRoomMessage) =>
        messageActions.appendLastMessage(chatRoomUuid, message),
      deleteMessage: (messageId: number) =>
        messageActions.deleteMessage(chatRoomUuid, messageId),
      updateMessage: (
        message: Pick<ChatRoomMessage, "id"> & Partial<ChatRoomMessage>,
      ) => messageActions.updateMessage(chatRoomUuid, message),
    }),
    [messageActions, chatRoomUuid, isMessaging],
  );
};
